import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import { useConvexAuth, useMutation } from "convex/react";
import { Building2, Clock, LogIn, Mail, Shield, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { GridBackground } from "@/components/ui/grid-background";
import { SlapLogo } from "@/components/ui/slap-logo";
import { Spinner } from "@/components/ui/spinner";
import { BRAND_NAME } from "@/lib/constants/brand";
import { handleConvexError } from "@/lib/errors";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { formatRelativeTime } from "@/lib/utils/format";
import {
	PENDING_INVITE_TOKEN_KEY,
	SIGN_IN_KEY,
} from "@/providers/auth-provider";

export const Route = createFileRoute("/invite/$token")({
	component: InviteAcceptPage,
});

function InviteAcceptPage() {
	const { token } = useParams({ from: "/invite/$token" });
	const { isAuthenticated } = useConvexAuth();
	const { signIn } = useAuth();
	const navigate = useNavigate();
	const [isAccepting, setIsAccepting] = useState(false);
	const [invite, setInvite] = useState<
		| {
				email: string;
				organizationName: string;
				roleName: string;
				expiresAt: number;
				status: "pending" | "accepted" | "expired";
		  }
		| null
		| undefined
	>(undefined);

	const getByToken = useMutation(api.features.invites.web.getByToken);
	const accept = useMutation(api.features.invites.web.accept);

	useEffect(() => {
		getByToken({ token })
			.then(setInvite)
			.catch(() => setInvite(null));
	}, [token, getByToken]);

	if (invite === undefined) {
		return (
			<InviteLayout>
				<LoadingState />
			</InviteLayout>
		);
	}

	if (invite === null) {
		return (
			<InviteLayout>
				<ErrorState
					title="Invalid invite link"
					description="This invite link is invalid or has been removed."
				/>
			</InviteLayout>
		);
	}

	if (invite.status === "accepted") {
		return (
			<InviteLayout>
				<ErrorState
					title="Invite already accepted"
					description="This invite has already been accepted."
				/>
			</InviteLayout>
		);
	}

	if (invite.status === "expired" || invite.expiresAt < Date.now()) {
		return (
			<InviteLayout>
				<ErrorState
					title="Invite expired"
					description="This invite link has expired. Please ask the organization admin to send a new one."
				/>
			</InviteLayout>
		);
	}

	const handleSignIn = () => {
		sessionStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
		sessionStorage.setItem(SIGN_IN_KEY, String(Date.now()));
		void signIn();
	};

	const handleAccept = async () => {
		setIsAccepting(true);
		try {
			await accept({ token });
			toast.success(<>You've joined {invite.organizationName}</>);
			void navigate({ to: "/" });
		} catch (error) {
			handleConvexError(error, "Failed to accept invite. Please try again.");
			setIsAccepting(false);
		}
	};

	const expiresIn = formatRelativeTime(invite.expiresAt);

	return (
		<InviteLayout>
			<div
				className={cn(
					"animate-slide-up-fade w-full max-w-sm",
					"[--offset:10px] [animation-delay:250ms] animation-duration-[1s]",
				)}
			>
				<Card>
					<CardHeader>
						<CardTitle className="text-lg">You've been invited</CardTitle>
						<CardDescription>
							Join{" "}
							<span className="font-medium text-foreground">
								{invite.organizationName}
							</span>{" "}
							on {BRAND_NAME}
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-3">
						<DetailRow
							icon={Building2}
							label="Organization"
							value={invite.organizationName}
						/>
						<DetailRow icon={Shield} label="Role" value={invite.roleName} />
						<DetailRow icon={Mail} label="Email" value={invite.email} />
						<DetailRow icon={Clock} label="Expires" value={expiresIn} />
					</CardContent>
					<CardFooter>
						{isAuthenticated ? (
							<Button
								className="w-full"
								onClick={handleAccept}
								disabled={isAccepting}
							>
								<UserPlus className="mr-2 size-4" />
								{isAccepting ? "Accepting..." : "Accept invite"}
							</Button>
						) : (
							<Button className="w-full" onClick={handleSignIn}>
								<LogIn className="mr-2 size-4" />
								Sign in to accept
							</Button>
						)}
					</CardFooter>
				</Card>
			</div>
		</InviteLayout>
	);
}

function InviteLayout({ children }: Readonly<{ children: React.ReactNode }>) {
	return (
		<>
			<GridBackground />
			<div className="relative flex min-h-screen w-full flex-col items-center overflow-y-auto">
				<div
					className={cn(
						"animate-slide-up-fade shrink-0 pt-10 text-primary",
						"[--offset:20px] animation-duration-[1.3s]",
					)}
				>
					<SlapLogo width={100} animate={false} />
				</div>
				<div className="flex w-full flex-1 items-start justify-center px-4 py-16">
					{children}
				</div>
			</div>
		</>
	);
}

function LoadingState() {
	return (
		<div className="flex flex-col items-center gap-4 pt-8">
			<Spinner className="size-8" />
			<p className="text-sm text-muted-foreground">Loading invite...</p>
		</div>
	);
}

function ErrorState({
	title,
	description,
}: Readonly<{
	title: string;
	description: string;
}>) {
	return (
		<div
			className={cn(
				"animate-slide-up-fade w-full max-w-sm",
				"[--offset:10px] [animation-delay:250ms] animation-duration-[1s]",
			)}
		>
			<Card>
				<CardHeader className="text-center">
					<CardTitle className="text-lg">{title}</CardTitle>
					<CardDescription>{description}</CardDescription>
				</CardHeader>
			</Card>
		</div>
	);
}

function DetailRow({
	icon: Icon,
	label,
	value,
}: Readonly<{
	icon: React.ComponentType<{ className?: string }>;
	label: string;
	value: string;
}>) {
	return (
		<div className="flex items-center gap-3 text-sm">
			<Icon className="size-4 shrink-0 text-muted-foreground" />
			<span className="text-muted-foreground">{label}</span>
			<span className="ml-auto truncate font-medium">{value}</span>
		</div>
	);
}
